import { apiVisitEngine } from '@/shared/api/client'
import type { IApiResponse } from '@/shared/types/api'
import { useQuery } from '@tanstack/react-query'
import type {
  IBloodGroup,
  IComponent,
  ICountry,
  IDonorCategory,
  IDeferralReason,
  IDepartment,
  IDonationType,
  IIcd10,
  ILaboratory,
  IObstetricAnamnesis,
  IRegion,
  IRhFactor,
  ISpecializedDonorType,
  IResultColumn,
  ITemplateType,
  ITransfusionAnamnesis,
  ITubeColor,
  IUnit,
  IVisitType,
} from '../types'
import type { IOrganizationType } from '@/entities/organization/model'

export const dictionaryApi = {
  /** Listed in full; like ICD-10 it may or may not arrive in the envelope. */
  getDepartments: async () => {
    const response = await apiVisitEngine.get<
      IDepartment[] | IApiResponse<IDepartment[]>
    >('v1/dict/departments')

    const body = response.data
    if (Array.isArray(body)) return body
    return Array.isArray(body.data) ? body.data : []
  },
  /**
   * Typeahead over the ICD-10 tree — answers a partial code or name, or the
   * first `limit` entries when no query is given.
   *
   * The endpoint is documented as returning a bare array but goes through the
   * same envelope as the other dictionaries in practice, so both shapes are
   * accepted and always unwrapped to a list.
   */
  getIcd10: async (q: string, limit: number) => {
    // An empty `q` is dropped so the endpoint answers with the plain list.
    const params = { q: q || undefined, limit }
    const response = await apiVisitEngine.get<
      IIcd10[] | IApiResponse<IIcd10[]>
    >('v1/dict/icd10', { params })

    const body = response.data
    if (Array.isArray(body)) return body
    return Array.isArray(body.data) ? body.data : []
  },
  getRegions: async () => {
    const response =
      await apiVisitEngine.get<IApiResponse<IRegion[]>>('v1/dict/regions')
    return response.data
  },
  getCountries: async () => {
    const response =
      await apiVisitEngine.get<IApiResponse<ICountry[]>>('v1/dict/countries')
    return response.data
  },
  getProcessStages: async () => {
    const response = await apiVisitEngine.get('v1/dict/process-stages')
    return response.data
  },
  getOwnershipTypes: async () => {
    const response = await apiVisitEngine.get('v1/dict/ownership-types')
    return response.data
  },
  getOrganizationTypes: async () => {
    const response = await apiVisitEngine.get<
      IApiResponse<IOrganizationType[]>
    >('v1/dict/organization-types')
    return response.data
  },
  getGovernmentBodies: async () => {
    const response = await apiVisitEngine.get('v1/dict/government-bodies')
    return response.data
  },
  getFacilities: async () => {
    const response = await apiVisitEngine.get('v1/dict/facilities')
    return response.data
  },
  getDonationTypes: async () => {
    const response = await apiVisitEngine.get<IApiResponse<IDonationType[]>>(
      'v1/dict/donation-types',
    )
    return response.data
  },
  getDonorCategories: async () => {
    const response = await apiVisitEngine.get<IApiResponse<IDonorCategory[]>>(
      'v1/dict/donor-categories',
    )
    return response.data
  },
  getVisitTypes: async () => {
    const response = await apiVisitEngine.get<IApiResponse<IVisitType[]>>(
      'v1/dict/visit-types',
    )
    return response.data
  },
  getDeferralReasons: async () => {
    const response = await apiVisitEngine.get<IApiResponse<IDeferralReason[]>>(
      'v1/dict/deferral-reasons',
    )
    return response.data
  },
  getDefectReasons: async () => {
    const response = await apiVisitEngine.get('v1/dict/defect-reasons')
    return response.data
  },
  getBloodGroups: async () => {
    const response = await apiVisitEngine.get<IApiResponse<IBloodGroup[]>>(
      'v1/dict/blood-groups',
    )
    return response.data
  },
  getTubeColors: async () => {
    const response = await apiVisitEngine.get<IApiResponse<ITubeColor[]>>(
      'v1/dict/tube-colors',
    )
    return response.data
  },
  getTemplateTypes: async () => {
    const response = await apiVisitEngine.get<IApiResponse<ITemplateType[]>>(
      'v1/dict/template-types',
    )
    return response.data
  },
  getRhFactors: async () => {
    const response =
      await apiVisitEngine.get<IApiResponse<IRhFactor[]>>('v1/dict/rh-factors')
    return response.data
  },
  getResultColumns: async () => {
    const response = await apiVisitEngine.get<IApiResponse<IResultColumn[]>>(
      'v1/dict/result-columns',
    )
    return response.data
  },
  getLaboratories: async () => {
    const response = await apiVisitEngine.get<IApiResponse<ILaboratory[]>>(
      'v1/dict/laboratories',
    )
    return response.data
  },
  /** Localized server-side; unwrapped like the other bare-array dictionaries. */
  getTransfusionAnamnesis: async () => {
    const response = await apiVisitEngine.get<
      ITransfusionAnamnesis[] | IApiResponse<ITransfusionAnamnesis[]>
    >('v1/dict/transfusion-anamnesis')

    const body = response.data
    if (Array.isArray(body)) return body
    return Array.isArray(body.data) ? body.data : []
  },
  getObstetricAnamnesis: async () => {
    const response = await apiVisitEngine.get<
      IObstetricAnamnesis[] | IApiResponse<IObstetricAnamnesis[]>
    >('v1/dict/obstetric-anamnesis')

    const body = response.data
    if (Array.isArray(body)) return body
    return Array.isArray(body.data) ? body.data : []
  },
  getComponents: async () => {
    const response = await apiVisitEngine.get<
      IComponent[] | IApiResponse<IComponent[]>
    >('v1/dict/components')

    const body = response.data
    if (Array.isArray(body)) return body
    return Array.isArray(body.data) ? body.data : []
  },
  /** Active types only, sorted by code. */
  getSpecializedDonorTypes: async () => {
    const response = await apiVisitEngine.get<
      IApiResponse<ISpecializedDonorType[]>
    >('v1/dict/specialized-donor-types')
    return response.data.data
  },
  getUnits: async () => {
    const response =
      await apiVisitEngine.get<IApiResponse<IUnit[]>>('v1/dict/units')
    return response.data
  },
}

export const useGetRegionsQuery = () => {
  return useQuery({
    queryKey: ['regions'],
    queryFn: () => dictionaryApi.getRegions(),
  })
}

export const useGetCountriesQuery = () => {
  return useQuery({
    queryKey: ['countries'],
    queryFn: () => dictionaryApi.getCountries(),
  })
}

export const useGetProcessStagesQuery = () => {
  return useQuery({
    queryKey: ['process-stages'],
    queryFn: () => dictionaryApi.getProcessStages(),
  })
}

export const useGetOwnershipTypesQuery = () => {
  return useQuery({
    queryKey: ['ownership-types'],
    queryFn: () => dictionaryApi.getOwnershipTypes(),
  })
}

export const useGetOrganizationTypesQuery = () => {
  return useQuery({
    queryKey: ['organization-types'],
    queryFn: () => dictionaryApi.getOrganizationTypes(),
  })
}

export const useGetGovernmentBodiesQuery = () => {
  return useQuery({
    queryKey: ['government-bodies'],
    queryFn: () => dictionaryApi.getGovernmentBodies(),
  })
}

export const useGetFacilitiesQuery = () => {
  return useQuery({
    queryKey: ['facilities'],
    queryFn: () => dictionaryApi.getFacilities(),
  })
}

export const useGetDonationTypesQuery = () => {
  return useQuery({
    queryKey: ['donation-types'],
    queryFn: () => dictionaryApi.getDonationTypes(),
  })
}

export const useGetDonorCategoriesQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['donor-categories'],
    queryFn: () => dictionaryApi.getDonorCategories(),
    enabled: options?.enabled,
  })
}

export const useGetVisitTypesQuery = () => {
  return useQuery({
    queryKey: ['visit-types'],
    queryFn: () => dictionaryApi.getVisitTypes(),
  })
}

export const useGetDeferralReasonsQuery = () => {
  return useQuery({
    queryKey: ['deferral-reasons'],
    queryFn: () => dictionaryApi.getDeferralReasons(),
  })
}

export const useGetDefectReasonsQuery = () => {
  return useQuery({
    queryKey: ['defect-reasons'],
    queryFn: () => dictionaryApi.getDefectReasons(),
  })
}

export const useGetBloodGroupsQuery = () => {
  return useQuery({
    queryKey: ['blood-groups'],
    queryFn: () => dictionaryApi.getBloodGroups(),
  })
}

export const useGetTubeColorsQuery = () => {
  return useQuery({
    queryKey: ['tube-colors'],
    queryFn: () => dictionaryApi.getTubeColors(),
  })
}

export const useGetTemplateTypesQuery = () => {
  return useQuery({
    queryKey: ['template-types'],
    queryFn: () => dictionaryApi.getTemplateTypes(),
  })
}

export const useGetRhFactorsQuery = () => {
  return useQuery({
    queryKey: ['rh-factors'],
    queryFn: () => dictionaryApi.getRhFactors(),
  })
}

export const useGetResultColumnsQuery = () => {
  return useQuery({
    queryKey: ['result-columns'],
    queryFn: () => dictionaryApi.getResultColumns(),
  })
}

export const useGetLaboratoriesQuery = () => {
  return useQuery({
    queryKey: ['laboratories'],
    queryFn: () => dictionaryApi.getLaboratories(),
  })
}

export const useGetSpecializedDonorTypesQuery = () => {
  return useQuery({
    queryKey: ['specialized-donor-types'],
    queryFn: () => dictionaryApi.getSpecializedDonorTypes(),
    staleTime: Infinity,
  })
}

export const useGetUnitsQuery = () => {
  return useQuery({
    queryKey: ['units'],
    queryFn: () => dictionaryApi.getUnits(),
  })
}

/** An empty query returns the first `limit` entries, so it is always fetched. */
export const useGetIcd10Query = (q: string, limit = 20) => {
  return useQuery({
    queryKey: ['icd10', q, limit],
    queryFn: () => dictionaryApi.getIcd10(q, limit),
    // The tree is effectively static; keep results across reopens of the modal.
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  })
}

export const useGetDepartmentsQuery = () => {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => dictionaryApi.getDepartments(),
  })
}

export const useGetTransfusionAnamnesisQuery = () => {
  return useQuery({
    queryKey: ['transfusion-anamnesis'],
    queryFn: () => dictionaryApi.getTransfusionAnamnesis(),
  })
}

export const useGetObstetricAnamnesisQuery = () => {
  return useQuery({
    queryKey: ['obstetric-anamnesis'],
    queryFn: () => dictionaryApi.getObstetricAnamnesis(),
  })
}

export const useGetComponentsQuery = () => {
  return useQuery({
    queryKey: ['components'],
    queryFn: () => dictionaryApi.getComponents(),
  })
}
